import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchResultPanelContext } from './SearchActivity';
import './SegmentedTabs.css';

const SegmentedTabs = ({
    options,
    selectedValue,
    onChange,
    fontSize = '14px',
    buttonPadding = '6px 0px',
    width = '100%',
    disabled = false,
}) => {
    return (
        <div className='segmented-control glassSegmentedControl' style={{ width }}>
            {options.map((option) => (
                <button
                    key={option.value}
                    type='button'
                    className={'segmented-button' + (selectedValue === option.value ? ' selected' : '')}
                    aria-pressed={selectedValue === option.value}
                    disabled={disabled}
                    style={{ fontSize, fontWeight: '500', padding: buttonPadding }}
                    onClick={() => { onChange(option.value) }}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
};

export const SegmentedTabPanels = ({
    resetKey,
    options,
    panels,
    defaultValue,
    controlPadding = '0px',
    fontSize = '14px',
    buttonPadding = '6px 0px',
    keepMounted = false,
    lazyMount = false,
    scrollTargetRef,
    scrollResetKey = resetKey,
    stickyControlTop,
    selectedValue: controlledValue,
    onChange,
    hideSingleOption = false,
    hideControls = false,
}) => {
    const optionValues = useMemo(() => options.map((option) => option.value), [options]);
    const initialValue = defaultValue ?? optionValues[0] ?? '';
    const [internalValue, setSelectedValue] = useState(initialValue);
    const selectedValue = controlledValue ?? internalValue;
    const [visitedValues, setVisitedValues] = useState([selectedValue]);
    const previousResetKeyRef = useRef(resetKey);
    const previousDefaultValueRef = useRef(defaultValue);

    useEffect(() => {
        const nextDefaultValue = defaultValue ?? optionValues[0] ?? '';
        const resetKeyChanged = previousResetKeyRef.current !== resetKey;
        const defaultValueChanged = previousDefaultValueRef.current !== defaultValue;

        previousResetKeyRef.current = resetKey;
        previousDefaultValueRef.current = defaultValue;

        if (resetKeyChanged || defaultValueChanged) {
            setVisitedValues([controlledValue ?? nextDefaultValue]);
        }

        setSelectedValue((currentValue) => {
            if (resetKeyChanged || defaultValueChanged) {
                return nextDefaultValue;
            }

            if (!optionValues.includes(currentValue)) {
                return nextDefaultValue;
            }

            return currentValue;
        });
    }, [controlledValue, defaultValue, optionValues, resetKey]);

    useEffect(() => {
        setVisitedValues(values => values.includes(selectedValue) ? values : [...values, selectedValue]);
    }, [selectedValue]);

    useEffect(() => {
        // The page supplies an anchor above its header clearance. This also works
        // inside a speaker drawer, where the document isn't the scroll container.
        scrollTargetRef?.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }, [scrollTargetRef, scrollResetKey, selectedValue]);

    return (
        <>
            {!hideControls && (!hideSingleOption || options.length > 1) && <div style={{
                padding: controlPadding,
                ...(stickyControlTop !== undefined ? {
                    position: 'sticky',
                    top: stickyControlTop,
                    zIndex: 109,
                    background: 'var(--app-background)',
                } : {}),
            }}>
                <SegmentedTabs
                    options={options}
                    selectedValue={selectedValue}
                    onChange={(value) => {
                        setSelectedValue(value);
                        onChange?.(value);
                    }}
                    fontSize={fontSize}
                    buttonPadding={buttonPadding}
                />
            </div>}
            {keepMounted ?
                options.map((option) => (
                    <div key={option.value} style={{ display: selectedValue === option.value ? 'block' : 'none' }}>
                        <SearchResultPanelContext.Provider value={selectedValue === option.value}>
                            {(!lazyMount || selectedValue === option.value || visitedValues.includes(option.value))
                                ? panels[option.value] ?? null : null}
                        </SearchResultPanelContext.Provider>
                    </div>
                ))
                :
                panels[selectedValue] ?? null
            }
        </>
    );
};

export default SegmentedTabs;
